import { version as currentVersion } from "../../package.json"
import { isPatchUpdate, requiresUpdatePrompt } from "@/services/app-update-policy"
import { showUpdateAvailableDialog } from "@/services/app-update-dialog"

const UPDATE_CHECK_INTERVAL_MS = 60 * 1000

type ApplyUpdate = (reloadPage?: boolean) => Promise<void>

let registration: ServiceWorkerRegistration | null = null
let applyUpdate: ApplyUpdate | null = null
let appReady = false
let activeSession = false
let updateCheckRunning = false
let applyWhenReady = false
let dismissedVersion: string | null = null
let updateCheckInterval: ReturnType<typeof setInterval> | null = null

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const response = await fetch(`/version.json?ts=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" }
    })
    if (!response.ok) return null

    const data = (await response.json()) as { version?: string }
    return data.version ?? null
  } catch (error) {
    console.error("[PWA] Failed to fetch deployed version", error)
    return null
  }
}

async function activateUpdate() {
  if (!applyUpdate) return
  applyWhenReady = false

  try {
    await applyUpdate(true)
  } catch (error) {
    console.error("[PWA] Failed to activate service worker update", error)
  }
}

async function requestUpdateActivation() {
  applyWhenReady = true

  try {
    await registration?.update()
    if (registration?.waiting) await activateUpdate()
  } catch (error) {
    applyWhenReady = false
    console.error("[PWA] Failed to prepare service worker update", error)
  }
}

function showUpdateDialog(pendingVersion: string) {
  if (!activeSession || dismissedVersion === pendingVersion) return

  showUpdateAvailableDialog({
    version: pendingVersion,
    onConfirm: async () => {
      await requestUpdateActivation()
    },
    onCancel: () => {
      dismissedVersion = pendingVersion
    }
  })
}

async function evaluateDeployedVersion() {
  const pendingVersion = await fetchDeployedVersion()
  if (!pendingVersion || pendingVersion === currentVersion) return

  if (isPatchUpdate(currentVersion, pendingVersion)) {
    console.log("[PWA] Patch update available; deferring until next startup", {
      currentVersion,
      pendingVersion
    })
    return
  }

  if (requiresUpdatePrompt(currentVersion, pendingVersion)) {
    console.log("[PWA] Minor or major update available", {
      currentVersion,
      pendingVersion
    })
    showUpdateDialog(pendingVersion)
  }
}

async function checkForUpdate() {
  if (!activeSession || !registration || updateCheckRunning) return

  updateCheckRunning = true
  try {
    void registration.update().catch((error) => {
      console.error("[PWA] Service worker update check failed", error)
    })
    await evaluateDeployedVersion()
  } catch (error) {
    console.error("[PWA] Update check failed", error)
  } finally {
    updateCheckRunning = false
  }
}

function onVisibilityChange() {
  if (document.visibilityState === "visible") {
    void checkForUpdate()
  }
}

function startActiveSessionChecks() {
  stopActiveSessionChecks()
  updateCheckInterval = setInterval(() => {
    void checkForUpdate()
  }, UPDATE_CHECK_INTERVAL_MS)

  document.addEventListener("visibilitychange", onVisibilityChange)
  void checkForUpdate()
}

function stopActiveSessionChecks() {
  if (updateCheckInterval) {
    clearInterval(updateCheckInterval)
    updateCheckInterval = null
  }

  document.removeEventListener("visibilitychange", onVisibilityChange)
}

function finishStartup() {
  if (!appReady || !registration || activeSession) return

  if (registration.waiting) {
    void activateUpdate()
    return
  }

  activeSession = true
  startActiveSessionChecks()
}

export function markPwaStartupComplete() {
  appReady = true
  finishStartup()
}

export function initializePwaUpdateRegistration(
  registeredServiceWorker: ServiceWorkerRegistration,
  updateServiceWorker: ApplyUpdate
) {
  registration = registeredServiceWorker
  applyUpdate = updateServiceWorker
  finishStartup()
}

export function handlePwaUpdateAvailable() {
  if (!activeSession || applyWhenReady) {
    void activateUpdate()
    return
  }

  void evaluateDeployedVersion()
}

export function teardownPwaUpdateService() {
  stopActiveSessionChecks()
  registration = null
  applyUpdate = null
  appReady = false
  activeSession = false
  updateCheckRunning = false
  applyWhenReady = false
  dismissedVersion = null
}
